import * as React from 'react';
import { useImperativeHandle, useRef, useState } from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';
import { LoadingSpinner } from '../loading-spinner';

type InlineTextInputMode = 'read' | 'write';

export interface InlineTextInputProps {
    dataId: string;
    ariaLabel: string;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    onFocus?: () => void;
    value?: string;
    width?: string;
    fontSize?: string;
    loading?: boolean;
    maxLength?: number;
    disabled?: boolean;
}

interface StyledInlineTextInputProps {
    width: string;
    isReadOnly: boolean;
    disabled?: boolean;
}

const StyledInlineTextInput = styled.div<StyledInlineTextInputProps>`
    width: ${(props: StyledInlineTextInputProps) => props.width};
    display: flex;
    border-radius: ${RADIUS.FORM_FIELD};
    border: 1px solid ${COLOR.MAIN.FORM_FIELD};

    ${(props: StyledInlineTextInputProps) =>
        props.isReadOnly &&
        `
    border-color: transparent;

    &:hover:not([disabled]) {
        background-color: ${COLOR.MAIN.FORM_FIELD};
        cursor: text;
        transition: background 0.2s ease 0s;
    }

    &[disabled] {
        opacity: 0.3;
    }
  `}
`;

interface StyledWriteableTextProps {
    fontSize: string;
    isReadOnly: boolean;
}

const StyledTextInput = styled.input<StyledWriteableTextProps>`
    font-size: ${(props: StyledWriteableTextProps) => props.fontSize};
    width: 100%;
    border: none;
    border-radius: inherit;
    padding: ${SPACING.SMALL};
    font-size: ${(props: StyledWriteableTextProps) => props.fontSize};
    background-color: inherit;
    transition: inherit;

    ${(props: StyledWriteableTextProps) =>
        props.isReadOnly &&
        `
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
    padding: ${SPACING.X_SMALL};
`;

export const InlineTextInput = React.forwardRef<HTMLInputElement, InlineTextInputProps>(
    (
        {
            value,
            onChange,
            onBlur,
            onFocus,
            width = '100%',
            fontSize = FONT_SIZE.SMALL,
            loading,
            dataId,
            maxLength,
            ariaLabel,
            disabled,
            ...props
        },
        ref,
    ) => {
        const [mode, setMode] = useState<InlineTextInputMode>('read');
        const inputRef = useRef<HTMLInputElement>(null);

        useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => inputRef.current);

        const handleBlur = (value: string) => {
            if (mode === 'write') {
                setMode('read');
                onBlur(value);
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && mode === 'write') {
                handleBlur(e.currentTarget.value);
            }
        };

        const handleFocus = () => {
            setMode('write');
            inputRef.current?.focus();
            onFocus?.();
        };

        return (
            <StyledInlineTextInput
                width={width}
                isReadOnly={mode === 'read'}
                onClick={handleFocus}
                data-test-id={dataId}
                disabled={disabled}
                {...props}
            >
                <StyledTextInput
                    value={value}
                    onBlur={(event) => handleBlur(event.target.value)}
                    onChange={(event) => onChange(event.target.value)}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    fontSize={fontSize}
                    readOnly={mode === 'read'}
                    isReadOnly={mode === 'read'}
                    data-test-id={`${dataId}-input`}
                    ref={inputRef}
                    maxLength={maxLength}
                    aria-label={ariaLabel}
                    disabled={disabled}
                />
                {loading && (
                    <StyledLoadingSpinner
                        color={'black'}
                        iconWidth={fontSize}
                        iconHeight={fontSize}
                        data-test-id={`${dataId}-loading-spinner`}
                    />
                )}
            </StyledInlineTextInput>
        );
    },
);
InlineTextInput.displayName = 'InlineTextInput';
